import type { ReactNode, CSSProperties } from "react";
import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { EhDataTableShell } from "@/components/dataTable/EhDataTableShell";
import { SortableHeader } from "@/components/dataTable/SortableHeader";
import { TableSortResetBar } from "@/components/dataTable/TableSortResetBar";
import {
  sortRowsByKey,
  toggleSort,
  type SortComparable,
  type SortState,
} from "@/components/dataTable/tableSort";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";

/** Sticky-Breite pro rechter Anheft-Spalte (gleich Projektetabelle / klassische App). */
const STICKY_END_COL_WIDTH_PX = 44;

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  /** When true, header becomes a sort control (unless sticky-end action column). */
  sortable?: boolean;
  /** Value used for client-side sorting; required when sortable. */
  sortValue?: (row: T) => SortComparable;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  emptyText?: ReactNode;
  className?: string;
  rowClassName?: (row: T) => string | undefined;
  onRowClick?: (row: T) => void;
  /** Tabellenkopf bleibt beim Seiten-Scroll unter dem App-Header sichtbar. */
  stickyHeader?: boolean;
  /**
   * Anzahl der rechten Spalten, die wie Aktionsspalten „angeheftet“ werden
   * (sticky right, z. B. 1 für eine Aktions-Spalte).
   */
  stickyEndColumnCount?: number;
  /** Controlled sort state. Omit for internal state when columns are sortable. */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  /**
   * When false, Table does not reorder rows (parent already sorted, e.g. server-side).
   * Default true.
   */
  clientSort?: boolean;
  /** Hide the built-in reset bar (e.g. parent renders its own). Default false. */
  hideSortResetBar?: boolean;
}

function TableHeadRow<T>({
  columns,
  pinned,
  dataColCount,
  pinRightOffset,
  sort,
  onSort,
}: {
  columns: Column<T>[];
  pinned: number;
  dataColCount: number;
  pinRightOffset: (pinnedIndex: number) => CSSProperties | undefined;
  sort: SortState | null;
  onSort: (key: string) => void;
}) {
  return (
    <tr>
      {columns.map((col, idx) => {
        const isPinned = pinned > 0 && idx >= dataColCount;
        const pinnedIdx = isPinned ? idx - dataColCount : -1;
        const canSort = Boolean(col.sortable) && !isPinned;
        return (
          <th
            key={col.key}
            scope="col"
            className={clsx(
              "px-3 py-2.5 text-left font-semibold",
              !isPinned && "eh-data-th",
              isPinned && "eh-data-action-cell",
              col.headerClassName,
            )}
            style={isPinned ? pinRightOffset(pinnedIdx) : undefined}
          >
            {canSort ? (
              <SortableHeader
                label={col.header}
                columnKey={col.key}
                sort={sort}
                onSort={onSort}
              />
            ) : (
              col.header
            )}
          </th>
        );
      })}
    </tr>
  );
}

export function Table<T>({
  columns,
  rows,
  rowKey,
  emptyText = "Keine Einträge.",
  className,
  rowClassName,
  onRowClick,
  stickyHeader = true,
  stickyEndColumnCount = 0,
  sort: controlledSort,
  onSortChange,
  clientSort = true,
  hideSortResetBar = false,
}: TableProps<T>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [uncontrolledSort, setUncontrolledSort] = useState<SortState | null>(null);

  const isControlled = controlledSort !== undefined;
  const sort = isControlled ? (controlledSort ?? null) : uncontrolledSort;

  const hasSortableColumns = columns.some((col) => col.sortable);

  const setSort = (next: SortState | null) => {
    if (!isControlled) setUncontrolledSort(next);
    onSortChange?.(next);
  };

  const handleSort = (key: string) => {
    setSort(toggleSort(sort, key, "asc"));
  };

  const pinned = Math.min(Math.max(0, stickyEndColumnCount), columns.length);
  const dataColCount = columns.length - pinned;

  const sortValueByKey = useMemo(() => {
    const map = new Map<string, (row: T) => SortComparable>();
    for (const col of columns) {
      if (col.sortable && col.sortValue) {
        map.set(col.key, col.sortValue);
      }
    }
    return map;
  }, [columns]);

  const displayRows = useMemo(() => {
    if (!clientSort || !sort) return rows;
    const getter = sortValueByKey.get(sort.key);
    if (!getter) return rows;
    return sortRowsByKey(rows, sort, (row, key) => {
      const g = sortValueByKey.get(key);
      return g ? g(row) : null;
    });
  }, [clientSort, rows, sort, sortValueByKey]);

  const syncKey = useMemo(
    () =>
      `${columns.map((c) => c.key).join("|")}|${displayRows.length}|${sort?.key ?? ""}|${sort?.direction ?? ""}`,
    [columns, displayRows.length, sort?.key, sort?.direction],
  );

  const floating = useFloatingProjectTableHeader(wrapRef, tableRef, {
    enabled: stickyHeader,
    syncKey,
  });

  function pinRightOffset(pinnedIndex: number): CSSProperties | undefined {
    if (pinned <= 0) return undefined;
    const fromEnd = pinned - 1 - pinnedIndex;
    return { right: fromEnd * STICKY_END_COL_WIDTH_PX };
  }

  const theadClass = clsx(
    "eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary",
  );

  const headRow = (
    <TableHeadRow
      columns={columns}
      pinned={pinned}
      dataColCount={dataColCount}
      pinRightOffset={pinRightOffset}
      sort={sort}
      onSort={handleSort}
    />
  );

  return (
    <>
      {!hideSortResetBar && hasSortableColumns ? (
        <TableSortResetBar visible={sort != null} onReset={() => setSort(null)} />
      ) : null}

      <EhDataTableShell ref={wrapRef} className={className}>
        <table ref={tableRef} className="eh-data-table min-w-full divide-y divide-separator text-sm">
          <thead className={theadClass}>{headRow}</thead>
          <tbody className="divide-y divide-separator bg-card">
            {displayRows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-sm text-label-secondary"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {displayRows.map((row) => (
              <tr
                key={rowKey(row)}
                className={clsx(
                  "eh-data-row align-top",
                  onRowClick && "cursor-pointer",
                  rowClassName?.(row),
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col, idx) => {
                  const isPinned = pinned > 0 && idx >= dataColCount;
                  const pinnedIdx = isPinned ? idx - dataColCount : -1;
                  return (
                    <td
                      key={col.key}
                      className={clsx(
                        "px-3 py-2.5 text-label",
                        !isPinned && "eh-data-body-cell",
                        isPinned && "eh-data-action-cell",
                        col.className,
                      )}
                      style={isPinned ? pinRightOffset(pinnedIdx) : undefined}
                    >
                      {col.render(row)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </EhDataTableShell>

      {stickyHeader &&
        floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <thead className={theadClass}>{headRow}</thead>
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
